/**
 * ProClientComponents.jsx
 * Shared client-related components used by ProDiary and ProRecordSale.
 * Extracted from ProDiary.jsx (Session 10) to avoid duplication.
 *
 * Exports:
 *   PatchTestChip     — coloured status chip for patch test state
 *   ClientSearchField — debounced search with dropdown results
 *   NewClientDialog   — modal form to create a new client record
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Chip, TextField, Paper, List,
  ListItemButton, ListItemText, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Grid, Alert, InputAdornment,
} from '@mui/material';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SearchIcon       from '@mui/icons-material/Search';
import PersonAddIcon    from '@mui/icons-material/PersonAdd';
import api from './axiosConfig';


// ── Patch test status chip ─────────────────────────────────────────────────

export function PatchTestChip({ status }) {
  if (!status || status === 'none') return null;
  const config = {
    valid:    { label: 'Patch test valid',    bgcolor: '#2e7d32', color: '#fff' },
    expired:  { label: 'Patch test expired',  bgcolor: '#BF360C', color: '#fff' },
    positive: { label: 'Patch test positive', bgcolor: '#c62828', color: '#fff' },
    pending:  { label: 'Patch test pending',  bgcolor: '#f57c00', color: '#fff' },
  };
  const cfg = config[status];
  if (!cfg) return null;
  return (
    <Chip
      size="small"
      icon={
        status === 'valid'
          ? <CheckCircleIcon  style={{ fontSize: 14, color: '#fff' }} />
          : <WarningAmberIcon style={{ fontSize: 14, color: '#fff' }} />
      }
      label={cfg.label}
      sx={{ bgcolor: cfg.bgcolor, color: cfg.color, fontWeight: 600, fontSize: '0.7rem' }}
    />
  );
}


// ── Client search field ────────────────────────────────────────────────────

export function ClientSearchField({ onClientSelected }) {
  const [query,       setQuery]       = useState('');
  const [results,     setResults]     = useState([]);
  const [searching,   setSearching]   = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const timerRef  = useRef(null);

  useEffect(() => {
    if (query.length < 2) { setResults([]); setShowResults(false); return; }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get('/pro-booking/clients/search', { params: { q: query } });
        setResults(res.data || []);
        setShowResults(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timerRef.current);
  }, [query]);

  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (client) => {
    onClientSelected(client);
    setQuery('');
    setResults([]);
    setShowResults(false);
  };

  return (
    <Box ref={searchRef} sx={{ position: 'relative' }}>
      <TextField
        fullWidth
        label="Search existing clients"
        placeholder="Type name or phone…"
        value={query}
        onChange={e => setQuery(e.target.value)}
        onFocus={() => results.length > 0 && setShowResults(true)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {searching
                ? <CircularProgress size={16} />
                : <SearchIcon fontSize="small" color="action" />}
            </InputAdornment>
          ),
        }}
      />

      {showResults && results.length > 0 && (
        <Paper elevation={4} sx={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1300,
          maxHeight: 280, overflowY: 'auto', mt: 0.5,
        }}>
          <List dense disablePadding>
            {results.map((client) => (
              <ListItemButton
                key={client.id}
                onClick={() => handleSelect(client)}
                sx={{ borderBottom: '1px solid #f0f0f0', '&:last-child': { borderBottom: 'none' } }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={600}>{client.full_name}</Typography>
                      <PatchTestChip status={client.patch_test?.status} />
                    </Box>
                  }
                  secondary={client.phone || ''}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}

      {showResults && query.length >= 2 && results.length === 0 && !searching && (
        <Paper elevation={4} sx={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1300, mt: 0.5,
        }}>
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No clients found for "{query}"
            </Typography>
          </Box>
        </Paper>
      )}
    </Box>
  );
}


// ── New client dialog ──────────────────────────────────────────────────────

export function NewClientDialog({ open, onClose, onCreated }) {
  const [form, setForm]       = useState({
    first_name: '', last_name: '', phone: '', email: '', allergies: '', notes: '',
  });
  const [errors,   setErrors]   = useState({});
  const [saving,   setSaving]   = useState(false);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({ first_name: '', last_name: '', phone: '', email: '', allergies: '', notes: '' });
      setErrors({});
      setApiError(null);
    }
  }, [open]);

  const handleField = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = 'Required';
    if (!form.phone.trim())      e.phone      = 'Required';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    setApiError(null);
    try {
      const res = await api.post('/pro-booking/clients', {
        first_name: form.first_name.trim(),
        last_name:  form.last_name.trim()  || undefined,
        phone:      form.phone.trim(),
        email:      form.email.trim()      || undefined,
        allergies:  form.allergies.trim()  || undefined,
        notes:      form.notes.trim()      || undefined,
      });
      onCreated(res.data);
      onClose();
    } catch (e) {
      setApiError(e.response?.data?.error || e.message || 'Failed to create client');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Client</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="First name *" name="first_name"
              value={form.first_name} onChange={handleField}
              error={!!errors.first_name} helperText={errors.first_name} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Last name" name="last_name"
              value={form.last_name} onChange={handleField} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Phone *" name="phone"
              value={form.phone} onChange={handleField}
              error={!!errors.phone} helperText={errors.phone} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Email" name="email" type="email"
              value={form.email} onChange={handleField} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Allergies / Sensitivities" name="allergies"
              value={form.allergies} onChange={handleField} multiline rows={2} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Notes" name="notes"
              value={form.notes} onChange={handleField} multiline rows={2} />
          </Grid>
          {apiError && (
            <Grid item xs={12}>
              <Alert severity="error">{apiError}</Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}>
          {saving ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}